export function roundMoney(num: number, decimalPlaces = 2) {
  const d = decimalPlaces || 0
  const m = Math.pow(10, d)
  const n = +(d ? num * m : num).toFixed(8) // Avoid rounding errors
  const i = Math.floor(n),
    f = n - i
  const e = 1e-8 // Allow for rounding errors in f
  const r =
    f > 0.5 - e && f < 0.5 + e ? (i % 2 == 0 ? i : i + 1) : Math.round(n)

  return d ? r / m : r
}

interface InboxFilters {
  search?: string
  location?: string
  selectedField?: string
  selectedAccountManagers?: Array<{ pkid: number | string }>
}

export interface InboxConversation {
  customer_id: string
  last_message_created: string
  last_ai_message_at?: string | null
  [key: string]: unknown
}

export function upsertInboxConversation(
  list: InboxConversation[],
  contact: InboxConversation
) {
  const existingIndex = list.findIndex(
    conversation => conversation.customer_id === contact.customer_id
  )

  if (existingIndex !== -1) {
    if (
      list[existingIndex].last_message_created === contact.last_message_created
    ) {
      return list
    }
    list.splice(existingIndex, 1)
  }

  list.unshift(contact)

  return list
}

export function buildInboxFilterParams(
  filters: InboxFilters,
  activeTab?: string
) {
  const params: Record<string, string> = {}

  const search = (filters.search || '').trim().replaceAll(/[()+]+/g, '')
  if (search) {
    params.search = search
    params.field = filters.selectedField ?? ''
  }

  if (filters.location !== undefined && filters.location !== 'all') {
    params.location = filters.location
  }

  const accountManagers = filters.selectedAccountManagers || []
  if (accountManagers.length) {
    params.account_managers = accountManagers
      .map(accountManager => accountManager.pkid)
      .join(',')
  }

  if (activeTab === 'ai') {
    params.ai_texts = 'true'
  }

  return params
}

interface InvoiceItem {
  description?: string | null
  amount?: number | string
}

/**
 * Whether an invoice's line items are worth showing as a breakdown. Most invoices carry a single,
 * description-less lump-sum item equal to the invoice total (amount-only sources: partner API, CSV,
 * consumer, paybytxt), which adds nothing next to the total already on screen. Only a genuinely
 * categorized invoice — more than one item, with at least one real description — earns the breakdown.
 */
export function hasInvoiceBreakdown(items?: InvoiceItem[] | null): boolean {
  if (!Array.isArray(items) || items.length < 2) {
    return false
  }

  return items.some(
    item => typeof item?.description === 'string' && !!item.description.trim()
  )
}

export interface ChatSocketMessage {
  id?: string | number | null
  sender?: unknown
}

/**
 * Whether a chat websocket frame is a customer message (`sender_id: null`) —
 * the only kind the unread badge counts.
 */
export function isInboundChatMessage(message: ChatSocketMessage): boolean {
  return !message?.sender
}

interface ChatNameSource {
  first_name?: unknown
  last_name?: unknown
}

function isChatNameSource(value: unknown): value is ChatNameSource {
  return typeof value === 'object' && value !== null
}

function chatPersonName(person: ChatNameSource): string {
  const first = typeof person.first_name === 'string' ? person.first_name : ''
  const last = typeof person.last_name === 'string' ? person.last_name : ''

  return [first, last].filter(part => part.length > 0).join(' ')
}

/**
 * Name on a chat row. A string sender is the optimistic outbound marker
 * and shows as You. An object sender uses its own name. A missing sender
 * uses the customer. A missing name part is left out.
 */
export function chatSenderName(sender: unknown, customer?: unknown): string {
  if (typeof sender === 'string') return 'You'

  if (isChatNameSource(sender)) return chatPersonName(sender)

  if (isChatNameSource(customer)) return chatPersonName(customer)

  return ''
}

/**
 * Whether a socket frame is one the message list does not already hold. The
 * push path has no dedup of its own, and an id-less frame cannot be matched,
 * so it counts as new.
 */
export function isNewChatMessage(
  messages: ChatSocketMessage[],
  message: ChatSocketMessage
): boolean {
  const id = message?.id

  if (id === undefined || id === null) return true

  return !(messages || []).some(existing => existing?.id === id)
}

export function slugify(value) {
  const result = String(value)

  return result
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9 -]/g, '') // Invalid chars
    .replace(/\s+/g, '-') // whitespace -> -
    .replace(/-+/g, '-') // -- -> -
}
